/**
 * DEFCAT P6 — build a nested tree from the flat two-axis entry list.
 *
 * The catalogue version read returns a flat `DefectCatalogueEntryV2[]`; each
 * entry carries `axis` ('type'|'location') and `parentEntryId`. The editor's
 * dual treeview needs a nested structure per axis, ordered by `sortOrder`
 * (falling back to name). Pure + unit-tested.
 */
import type { DefectCatalogueEntryV2 } from "@aeriosiq/types";
import { locText } from "./locText.js";

export interface TreeNode {
  entry: DefectCatalogueEntryV2;
  children: TreeNode[];
  /**
   * DEFCAT-R2-04/R2-13 — synthetic L0 root: a *reference* to a global component
   * group (`companies_db.global_component_groups`), NOT a catalogue entry. L0
   * nodes are read-only here (selectable for context, never editable / retirable
   * / reorderable). The node id is `l0:<componentGroupId>` so it never collides
   * with a real `entryId`; `componentGroupId === null` ⇒ the fallback
   * "(no component group)" bucket. `entry` is a thin stand-in carrying the L0
   * display name so the existing TreePanel/locText paths render it unchanged.
   */
  l0?: { componentGroupId: string | null };
}

/** Synthetic-node id for an L0 component-group root. */
export function l0NodeId(componentGroupId: string | null): string {
  return `l0:${componentGroupId ?? "__none__"}`;
}

/** True when an entryId addresses a synthetic L0 reference node. */
export function isL0NodeId(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith("l0:");
}

/**
 * DEFCAT-R2-13 — id translation across the L0 wire boundary.
 *
 * The location tree (and therefore the Dependencies overlay + the inheritance
 * walk-up) addresses an L0 component group by its SYNTHETIC node id
 * (`l0:<componentGroupId>`). The taxonomy PUT/read wire, however, carries the
 * RAW component-group UUID in `locationEntryId` (the BE validates it against
 * the set of group ids referenced by the version's location entries, and
 * branch-clones keep these ids un-remapped — design note rule 7).
 *
 * To keep resolve + save + load in agreement we normalise EVERYTHING to the
 * synthetic id internally and translate only at the wire edge:
 *   - on LOAD  : `rawL0ToNodeId(p.locationEntryId, l0Ids)` — raw cg uuid → `l0:`
 *   - on SAVE  : `nodeIdToRawL0(p.locationEntryId)`        — `l0:` → raw cg uuid
 *
 * `referencedL0GroupIds(entries)` is the authoritative set of raw L0 uuids the
 * version references; only those raw ids are recognised as L0 on load (a stray
 * non-L0 entryId is left untouched).
 */

/** Strip the `l0:` synthetic prefix back to the raw component-group UUID. */
export function nodeIdToRawL0(id: string): string {
  return isL0NodeId(id) ? id.slice("l0:".length) : id;
}

/**
 * Translate a wire `locationEntryId` to the internal node id. When `id` is a
 * raw component-group UUID referenced by the version (`l0Ids`), it becomes the
 * synthetic `l0:<id>`; everything else (real location entryIds, foreign ids)
 * passes through unchanged.
 */
export function rawL0ToNodeId(id: string, l0Ids: ReadonlySet<string>): string {
  return l0Ids.has(id) ? l0NodeId(id) : id;
}

/**
 * The set of raw component-group UUIDs referenced by the version's location
 * entries (via `componentGroupId`, inherited down the parent chain) — i.e. the
 * exact ids the BE accepts as L0 allocation targets. The `null`/fallback bucket
 * has no uuid and is therefore never a wire L0 target.
 */
export function referencedL0GroupIds(
  entries: DefectCatalogueEntryV2[],
): Set<string> {
  const byId = new Map<string, DefectCatalogueEntryV2>();
  for (const e of entries) if (e.axis === "location") byId.set(e.entryId, e);
  const ids = new Set<string>();
  for (const e of byId.values()) {
    const cg = l0GroupOfLocationEntry(e, byId);
    if (cg != null && cg.length > 0) ids.add(cg);
  }
  return ids;
}

function sortKey(e: DefectCatalogueEntryV2): [number, string] {
  // taxonomy@1.4.0 / lyse@7.14.0 (BE #169) — the BE now returns `sortOrder` on
  // every catalogue entry, so it's a typed field on the V2 shape; read directly.
  const so = e.sortOrder;
  return [typeof so === "number" ? so : 0, locText(e.name, "")];
}

function compare(a: DefectCatalogueEntryV2, b: DefectCatalogueEntryV2): number {
  const [sa, na] = sortKey(a);
  const [sb, nb] = sortKey(b);
  if (sa !== sb) return sa - sb;
  return na.localeCompare(nb);
}

/**
 * Build the roots-with-children tree for a single axis. Entries whose
 * `parentEntryId` is unknown (or null) become roots. Cycles are guarded.
 */
export function buildAxisTree(
  entries: DefectCatalogueEntryV2[],
  axis: "type" | "location",
): TreeNode[] {
  const axisEntries = entries.filter((e) => e.axis === axis);
  const byId = new Map<string, DefectCatalogueEntryV2>();
  for (const e of axisEntries) byId.set(e.entryId, e);

  const childrenOf = new Map<string, DefectCatalogueEntryV2[]>();
  const roots: DefectCatalogueEntryV2[] = [];
  for (const e of axisEntries) {
    const pid = e.parentEntryId;
    if (pid != null && pid.length > 0 && byId.has(pid)) {
      const arr = childrenOf.get(pid) ?? [];
      arr.push(e);
      childrenOf.set(pid, arr);
    } else {
      roots.push(e);
    }
  }

  const seen = new Set<string>();
  function toNode(e: DefectCatalogueEntryV2): TreeNode {
    seen.add(e.entryId);
    const kids = (childrenOf.get(e.entryId) ?? [])
      .filter((c) => !seen.has(c.entryId))
      .sort(compare)
      .map(toNode);
    return { entry: e, children: kids };
  }

  return roots.sort(compare).map(toNode);
}

/** Flatten a tree to entryIds in depth-first order (self + descendants). */
export function descendantIds(node: TreeNode): string[] {
  const out: string[] = [node.entry.entryId];
  for (const c of node.children) out.push(...descendantIds(c));
  return out;
}

/**
 * Resolve the L0 component group a location entry hangs under. A depth-0
 * location entry carries its own `componentGroupId`; deeper entries inherit it
 * from their nearest ancestor that has one (Enercon: L0→L1 group→L2, where the
 * L1 group carries the componentGroupId). Walks `parentEntryId` to the root.
 */
export function l0GroupOfLocationEntry(
  entry: DefectCatalogueEntryV2,
  byId: ReadonlyMap<string, DefectCatalogueEntryV2>,
): string | null {
  const seen = new Set<string>();
  let cur: DefectCatalogueEntryV2 | undefined = entry;
  while (cur && !seen.has(cur.entryId)) {
    seen.add(cur.entryId);
    const cg = cur.componentGroupId;
    if (cg != null && cg.length > 0) return cg;
    const pid: string | null | undefined = cur.parentEntryId;
    cur = pid != null && pid.length > 0 ? byId.get(pid) : undefined;
  }
  return null;
}

/**
 * DEFCAT-R2-04/R2-13 — build the L0-ROOTED location tree.
 *
 * Roots are the distinct global component groups referenced by the catalogue's
 * location entries (via `componentGroupId`, inherited down the parent chain).
 * Each root's children are the catalogue location sub-trees that belong to it.
 * Location entries with no resolvable componentGroupId fall under a synthetic
 * "(no component group)" root.
 *
 * Works for both shapes:
 *  - Nordex/AERO flat: L2 entries sit directly under L0 (parentEntryId=null but
 *    componentGroupId set) → they become direct children of their L0 root.
 *  - Enercon: L0 → L1 group → L2; the top catalogue entry (the L1 group) hangs
 *    under its L0 root, with its own sub-tree intact.
 *
 * `groupName(componentGroupId)` resolves the L0 display name (from
 * `assets/v1 component-groups`); returns null/undefined when unknown, in which
 * case the node renders a placeholder + the componentGroupId is preserved.
 */
export function buildLocationTreeWithL0(
  entries: DefectCatalogueEntryV2[],
  groupName?: (componentGroupId: string) => string | null | undefined,
): TreeNode[] {
  // Per-axis nested location tree (real catalogue entries only).
  const flatRoots = buildAxisTree(entries, "location");

  const byId = new Map<string, DefectCatalogueEntryV2>();
  for (const e of entries) {
    if (e.axis === "location") byId.set(e.entryId, e);
  }

  // Group each *catalogue root* sub-tree by the L0 it belongs to. We bucket at
  // the catalogue-root level: a sub-tree's L0 is its root entry's resolved L0
  // (children inherit it, so the whole sub-tree shares one L0 root).
  const buckets = new Map<string, TreeNode[]>(); // l0NodeId → sub-trees
  const l0Order: { id: string; cg: string | null }[] = [];
  function bucketKey(cg: string | null): string {
    const id = l0NodeId(cg);
    if (!buckets.has(id)) {
      buckets.set(id, []);
      l0Order.push({ id, cg });
    }
    return id;
  }

  for (const root of flatRoots) {
    const cg = l0GroupOfLocationEntry(root.entry, byId);
    buckets.get(bucketKey(cg))?.push(root);
  }

  // Synthesize one read-only TreeNode per L0 root. Order: named groups first
  // (alphabetical by display name), then the "(no component group)" fallback.
  function nameFor(cg: string | null): string {
    if (cg === null) return "(no component group)";
    const n = groupName?.(cg);
    return n != null && n.length > 0 ? n : `Component group ${cg.slice(0, 8)}…`;
  }

  const ordered = [...l0Order].sort((a, b) => {
    // Fallback bucket sorts last.
    if (a.cg === null) return b.cg === null ? 0 : 1;
    if (b.cg === null) return -1;
    return nameFor(a.cg).localeCompare(nameFor(b.cg));
  });

  return ordered.map(({ id, cg }) => {
    const children = buckets.get(id) ?? [];
    const synthetic: DefectCatalogueEntryV2 = {
      entryId: id,
      defectClassId: "",
      name: { en: nameFor(cg) },
      groupableByDefault: false,
      axis: "location",
      parentEntryId: null,
      componentGroupId: cg,
    } as unknown as DefectCatalogueEntryV2;
    return { entry: synthetic, children, l0: { componentGroupId: cg } };
  });
}
